import { forwardRef } from "react";
import "./LoadingSpinner.css";

export interface LoadingSpinnerProps {
  /** Size of the spinner in pixels */
  size?: number;
  /** Additional CSS class name */
  className?: string;
  /** Aria label for accessibility */
  ariaLabel?: string;
}

/**
 * LoadingSpinner component
 *
 * A spinning loader indicator using SVG animation.
 * Used across the design system for loading states.
 *
 * @example
 * ```tsx
 * <LoadingSpinner size={24} />
 * <LoadingSpinner size={48} ariaLabel="Loading content" />
 * ```
 */
export const LoadingSpinner = forwardRef<SVGSVGElement, LoadingSpinnerProps>(
  ({ size = 24, className, ariaLabel = "Loading" }, ref) => {
    return (
      <svg
        ref={ref}
        className={`indigo-loading-spinner ${className || ""}`}
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        width={size}
        height={size}
        aria-label={ariaLabel}
        aria-hidden={!ariaLabel}
        role="status"
      >
        <circle
          style={{ opacity: 0.25 }}
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          style={{ opacity: 0.75 }}
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    );
  },
);

LoadingSpinner.displayName = "LoadingSpinner";
